import type { CasinoIcon, IncludeTags } from '@swf/casino';
import dropWinsIcon from '@swf/core/assets/casino/drops-wins.png';
import IcnHero from '@swf/core/assets/casino/icnHero.jpg';
import IcnHeroSquare from '@swf/core/assets/casino/icnHeroSquare.png';
import IcnHeroWide from '@swf/core/assets/casino/icnHeroWide.png';
import IcnLive from '@swf/core/assets/casino/icnLive.jpg';
import IcnPoster from '@swf/core/assets/casino/icnPoster.jpg';
import IcnSquare from '@swf/core/assets/casino/icnSquare.jpg';
import IcnStandard from '@swf/core/assets/casino/icnStandard.jpg';
import IcnWide from '@swf/core/assets/casino/icnWide.jpg';
import jackPotKingIcon from '@swf/core/assets/casino/jackpot-king.png';
import mustGoIcon from '@swf/core/assets/casino/must-go.png';
import partyPotIcon from '@swf/core/assets/casino/party-pot.png';
import type { CasinoGameImage, TileType } from '@swf/core/types/CasinoGame';

export const iconMeta: Record<
  CasinoIcon,
  {
    ims: CasinoIcon[];
    animated?: boolean;
    fallback?: HTMLImageElement;
    mobile?: number;
    desktop?: number;
  }
> = {
  icnForeground: { ims: ['icnForeground'], mobile: 212, desktop: 350 },
  icnBackground: { ims: ['icnBackground'], mobile: 237, desktop: 398 },
  icnReels: { ims: ['icnReels'], mobile: 290, desktop: 500 },
  icnFrame: { ims: ['icnFrame'], mobile: 320, desktop: 533 },
  icnAnimatedHero: { ims: ['icnAnimatedHero'] },
  icnAnimated: {
    ims: ['icnAnimated', 'icnStandard'],
    fallback: IcnHeroSquare,
    animated: true
  },
  icnAnimatedSquare: {
    ims: ['icnAnimatedSquare', 'icnSquare'],
    animated: true
  },
  icnAnimatedHeroSquare: {
    ims: ['icnAnimatedHeroSquare', 'icnHeroSquare'],
    animated: true
  },
  icnAnimatedHeroWide: {
    ims: ['icnAnimatedHeroWide', 'icnHeroWide'],
    animated: true
  },
  icnAnimatedWide: {
    ims: ['icnAnimatedWide', 'icnWide'],
    animated: true
  },
  icnHero: { ims: ['icnHero'], fallback: IcnHero },
  icnHeroSquare: {
    ims: ['icnHeroSquare'],
    mobile: 235,
    desktop: 373,
    fallback: IcnHeroSquare
  },
  icnHeroWide: {
    ims: ['icnHeroWide'],
    mobile: 404,
    desktop: 1155,
    fallback: IcnHeroWide
  },
  icnLive: {
    ims: ['icnLive'],
    fallback: IcnLive
  },
  icnStandard: { ims: ['icnStandard'], fallback: IcnStandard },
  icnOnTable: { ims: ['icnOnTable'] },
  icnPoster: {
    ims: ['icnPoster'],
    mobile: 127,
    desktop: 178,
    fallback: IcnPoster
  },
  icnSquare: {
    ims: ['icnSquare'],
    mobile: 127,
    desktop: 178,
    fallback: IcnSquare
  },
  icnWide: { ims: ['icnWide'], mobile: 265, desktop: 756, fallback: IcnWide },
  icnRace: { ims: ['icnRace', 'icnSquare'] },
  icnGameTrailerVideo: {
    ims: ['icnGameTrailerIcon', 'icnGameTrailerVideo']
  },
  icnGameTrailerIcon: {
    ims: ['icnGameTrailerIcon', 'icnGameTrailerVideo']
  }
};

export const needsVideoTag = (path: string) => path.match(new RegExp('mp4', 'i'));

export const getIconWidth = (icon: CasinoIcon, isMobile: boolean) =>
  iconMeta[icon].ims.reduce(
    (result: number | undefined | null, item: CasinoIcon) =>
      result || iconMeta[item][isMobile ? 'mobile' : 'desktop'],
    null
  );

export const getIconFallback = (icon: CasinoIcon) =>
  iconMeta[icon].ims.reduce(
    (result: HTMLImageElement | undefined | null, item: CasinoIcon) =>
      result || iconMeta[item].fallback,
    null
  );

export const getSLIconFallback = (tileType: TileType) => {
  const tileTypeFallbacks = { poster: IcnPoster, wide: IcnWide };
  return tileTypeFallbacks[tileType];
};

export const getIconClassKeys = (icon: CasinoIcon) =>
  iconMeta[icon].ims.reduce((result: string[], item: CasinoIcon) => {
    result.push(item);
    return result;
  }, []);

export const getRenderIcon = (icon: CasinoIcon, isMobile: boolean, images: CasinoGameImage) =>
  iconMeta[icon].ims.reduce((result: string | undefined | null, item: CasinoIcon) => {
    const check = iconMeta[item];
    return check.animated && isMobile ? null : result || images[item];
  }, null);

export const getMarketingTagIcon = (tags: IncludeTags) => {
  for (const tag of tags) {
    const lowerTag = tag.toLowerCase();

    if (lowerTag.includes('partypots')) {
      return partyPotIcon;
    } else if (lowerTag.includes('jackpot king')) {
      return jackPotKingIcon;
    } else if (lowerTag.includes('must go')) {
      return mustGoIcon;
    } else if (lowerTag.includes('dropsandwins')) {
      return dropWinsIcon;
    }
  }

  return '';
};
